import type {
    ClientCalendarEvent as GqlCalendarEvent,
    EditOnSend as GqlEditOnSend,
    Attendee,
    BusyType as GqlBusyType,
    ResponseType,
    ConflictResolutionType,
    RequestedAttendanceMode,
    VirtualAppointmentType,
    VirtualEventType,
    OnlineMeeting,
} from 'owa-graph-schema';
import type CalendarItem from 'owa-service/lib/contract/CalendarItem';
import getDisplayPreviewText from './utils/getDisplayPreviewText';
import type { MailboxInfo } from 'owa-client-types';
import { cleanTimeZoneId } from 'owa-datetime-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getConfig } from 'owa-service/lib/config';
import { getISOString, parse, type OwaDate } from 'owa-datetime';
import {
    IsAppendOnSendExtendedProperty,
    IsPrependOnSendExtendedProperty,
} from 'owa-calendar-types/lib/types/IsEditOnSendExtendedProperty';
import { IsBookedFreeBlocksExtendedProperty } from 'owa-calendar-types/lib/types/IsBookedFreeBlocksExtendedProperty';
import { CollabSpaceExtendedProperty } from 'owa-calendar-types/lib/types/CollabSpaceExtendedProperty';
import { ConflictResolutionExtendedProperty } from 'owa-calendar-types/lib/types/ConflictResolutionExtendedProperty';
import { VirtualAppointmentExtendedProperty } from 'owa-calendar-types/lib/types/VirtualAppointmentExtendedProperty';
import { IsPresenterExtendedProperty } from 'owa-calendar-types/lib/types/IsPresenterExtendedProperty';
import {
    ClpLabelExtendedProperty,
    ModificationExtendedProperty,
} from 'owa-calendar-types/lib/types/ClpLabelExtendedProperty';
import { UserDefinedImportanceScoreExtendedProperty } from 'owa-calendar-types/lib/types/UserDefinedImportanceScoreExtendedProperty';
import { CopilotImportanceScoreExtendedProperty } from 'owa-calendar-types/lib/types/CopilotImportanceScoreExtendedProperty';
import { CopilotImportanceReasonExtendedProperty } from 'owa-calendar-types/lib/types/CopilotImportanceReasonExtendedProperty';
import { CalendarAITimeInsightsExtendedProperty } from 'owa-calendar-types/lib/types/CalendarAITimeInsightsExtendedProperty';
import { CopilotImportanceReasonShortExtendedProperty } from 'owa-calendar-types/lib/types/CopilotImportanceReasonShortExtendedProperty';
import { CopilotImportancePromptVersionExtendedProperty } from 'owa-calendar-types/lib/types/CopilotImportancePromptVersionExtendedProperty';
import { convertEditOnSendToGql } from 'convert-edit-on-send/lib/convertEditOnSendToGql';
import { convertInboxRemindersToGql } from './convertInboxRemindersToGql';
import { convertRecurrenceTypeToGql } from './convertRecurrenceTypeToGql';
import { convertDocLinkToGql, convertAttachmentToGql } from 'convert-attachment';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { getLocationDisplayWithAddressText } from 'owa-location-display-text';
import { VirtualEventExtendedProperty } from 'owa-calendar-types/lib/types/VirtualEventExtendedProperty';
import {
    SchedulingServiceMeetingOptionsUrlExtendedProperty,
    SchedulingServiceUpdateUrlExtendedProperty,
} from 'teams-meeting-provider';
import { MeetingTemplateIdExtendedProperty } from 'owa-calendar-types/lib/types/MeetingTemplateIdExtendedProperty';
import { EventPollIdExtendedProperty } from 'owa-calendar-types/lib/types/EventPollIdExtendedProperty';
import { EventPollChoicesExtendedProperty } from 'owa-calendar-types/lib/types/EventPollChoicesExtendedProperty';
import { EventPollMetadataExtendedProperty } from 'owa-calendar-types/lib/types/EventPollMetadataExtendedProperty';
import { EventPollTimeZoneExtendedProperty } from 'owa-calendar-types/lib/types/EventPollTimeZoneExtendedProperty';
import { EventPollDurationExtendedProperty } from 'owa-calendar-types/lib/types/EventPollDurationExtendedProperty';
import { EventPollFinalizedChoiceExtendedProperty } from 'owa-calendar-types/lib/types/EventPollFinalizedChoiceExtendedProperty';
import { EventPollUpdateUrlExtendedProperty } from 'owa-calendar-types/lib/types/EventPollUpdateUrlExtendedProperty';
import { EventPollVotingUrlExtendedProperty } from 'owa-calendar-types/lib/types/EventPollVotingUrlExtendedProperty';
import { EventPollExpirationExtendedProperty } from 'owa-calendar-types/lib/types/EventPollExpirationExtendedProperty';
import { EventPreparationRecordExtendedProperty } from 'owa-calendar-types/lib/types/EventPreparationRecordExtendedProperty';
import { IntentExtendedProperty } from 'owa-calendar-types/lib/types/IntentExtendedProperty';
import { CopilotManagedEventTypeExtendedProperty } from 'owa-calendar-types/lib/types/CopilotManagedEventTypeExtendedProperty';
import { CopilotAutoReschedulingCountExtendedProperty } from 'owa-calendar-types/lib/types/CopilotAutoReschedulingCountExtendedProperty';
import {
    TeamRhythmEventTypeExtendedProperty,
    LegacyEventifyEventTypeExtendedProperty,
} from 'owa-calendar-types/lib/types/TeamRhythmExtendedProperty';

/**
 * Converts a CalendarItem instance to GqlCalendarEvent. This function will be used mostly from service
 * functions to convert the returned CalendarItem service objects to GQLCalendarEvent to be returned from
 * the worker.
 * @param calendarItem The calendar item (service object) to convert
 * @param mailboxInfo The mailbox info for the calendar event
 * @returns The converted CalendarEvent object
 */
export function mapOWSCalendarItemToGqlCalendarEvent(
    calendarItem: CalendarItem,
    mailboxInfo?: MailboxInfo
): GqlCalendarEvent {
    // Additional (Extended) Properties
    let appendOnSend: GqlEditOnSend[] = [];
    let prependOnSend: GqlEditOnSend[] = [];
    let isBookedFreeBlocks: boolean = false;
    let collabSpace: string | null = null;
    let clpLabelProperty: string | null = null;
    let clpLabelModificationHeader: string | null = null;
    let conflictResolution: ConflictResolutionType | null = null;
    let virtualAppointment: VirtualAppointmentType | null = null;
    let virtualEvent: VirtualEventType | null = null;
    let meetingTemplateId: string | null = null;
    let onlineMeeting: OnlineMeeting = {};
    let eventPollId: string | undefined = undefined;
    let eventPollChoices: string[] | undefined = undefined;
    let eventPollMetadata: string | undefined = undefined;
    let eventPollTimeZone: string | undefined = undefined;
    let eventPollDuration: number | undefined = undefined;
    let eventPollFinalizedChoice: string | undefined = undefined;
    let eventPollUpdateUrl: string | undefined = undefined;
    let eventPollVotingUrl: string | undefined = undefined;
    let eventPollExpiration: string | undefined = undefined;
    let userDefinedImportanceScore: number | null = null;
    let copilotImportanceScore: number | null = null;
    let copilotImportanceReason: string | null = null;
    let copilotImportanceReasonShort: string | null = null;
    let copilotImportancePromptVersion: string | null = null;
    let isPresenter: boolean = false;
    let intent: string | null = null;
    let copilotManagedEventType: number | null = null;
    let calendarAITimeInsights: string | undefined = undefined;
    let copilotAutoReschedulingCount: number | null = null;
    let teamRhythmEventType: string | null = null;
    let eventPreparationRecord: string | null = null;
    const onlineMeetingProviderProperties = [];

    if (calendarItem?.ExtendedProperty) {
        for (const item of calendarItem.ExtendedProperty) {
            const propertyName = item.ExtendedFieldURI?.PropertyName;
            const value = item.Value;
            const values = item.Values; // This is used for multi-valued properties

            switch (propertyName) {
                case IsAppendOnSendExtendedProperty.PropertyName:
                    if (value) {
                        try {
                            appendOnSend = JSON.parse(value)?.map(convertEditOnSendToGql);
                        } catch {
                            appendOnSend = [];
                        }
                    }
                    break;
                case IsPrependOnSendExtendedProperty.PropertyName:
                    if (value) {
                        try {
                            prependOnSend = JSON.parse(value)?.map(convertEditOnSendToGql);
                        } catch {
                            prependOnSend = [];
                        }
                    }
                    break;
                case ConflictResolutionExtendedProperty.PropertyName:
                    if (value) {
                        try {
                            conflictResolution = JSON.parse(value);
                        } catch {
                            conflictResolution = null;
                        }
                    }
                    break;
                case VirtualAppointmentExtendedProperty.PropertyName:
                    if (value) {
                        try {
                            virtualAppointment = JSON.parse(value);
                        } catch {
                            virtualAppointment = null;
                        }
                    }
                    break;
                case VirtualEventExtendedProperty.PropertyName:
                    if (value) {
                        try {
                            virtualEvent = JSON.parse(value);
                        } catch {
                            virtualEvent = null;
                        }
                    }
                    break;
                case MeetingTemplateIdExtendedProperty.PropertyName:
                    meetingTemplateId = value;
                    break;
                case IsBookedFreeBlocksExtendedProperty.PropertyName:
                    if (!!value) {
                        isBookedFreeBlocks = true;
                    }
                    break;
                case IsPresenterExtendedProperty.PropertyName:
                    if (!!value) {
                        isPresenter = true;
                    }
                    break;
                case UserDefinedImportanceScoreExtendedProperty.PropertyName:
                    userDefinedImportanceScore = value;
                    break;
                case CopilotImportanceScoreExtendedProperty.PropertyName:
                    copilotImportanceScore = value;
                    break;
                case CopilotImportanceReasonExtendedProperty.PropertyName:
                    copilotImportanceReason = value;
                    break;
                case CopilotImportanceReasonShortExtendedProperty.PropertyName:
                    copilotImportanceReasonShort = value;
                    break;
                case CopilotImportancePromptVersionExtendedProperty.PropertyName:
                    copilotImportancePromptVersion = value;
                    break;
                case IntentExtendedProperty.PropertyName:
                    intent = value;
                    break;
                case CopilotManagedEventTypeExtendedProperty.PropertyName:
                    if (value) {
                        copilotManagedEventType = parseInt(value);
                    }
                    break;
                case CopilotAutoReschedulingCountExtendedProperty.PropertyName:
                    if (value) {
                        copilotAutoReschedulingCount = parseInt(value);
                    }
                    break;
                case EventPollIdExtendedProperty.PropertyName:
                    eventPollId = value;
                    break;
                case EventPollChoicesExtendedProperty.PropertyName:
                    if (values) {
                        eventPollChoices = values;
                    }
                    break;
                case EventPollMetadataExtendedProperty.PropertyName:
                    eventPollMetadata = value;
                    break;
                case EventPollTimeZoneExtendedProperty.PropertyName:
                    eventPollTimeZone = value;
                    break;
                case EventPollDurationExtendedProperty.PropertyName:
                    if (value) {
                        eventPollDuration = parseInt(value);
                    }
                    break;
                case EventPollFinalizedChoiceExtendedProperty.PropertyName:
                    eventPollFinalizedChoice = value;
                    break;
                case EventPollUpdateUrlExtendedProperty.PropertyName:
                    eventPollUpdateUrl = value;
                    break;
                case EventPollVotingUrlExtendedProperty.PropertyName:
                    eventPollVotingUrl = value;
                    break;
                case EventPollExpirationExtendedProperty.PropertyName:
                    eventPollExpiration = value;
                    break;
                case CollabSpaceExtendedProperty.PropertyName:
                    collabSpace = value;
                    break;
                case ClpLabelExtendedProperty.PropertyName:
                    clpLabelProperty = value;
                    break;
                case ModificationExtendedProperty.PropertyName:
                    clpLabelModificationHeader = value;
                    break;
                case SchedulingServiceMeetingOptionsUrlExtendedProperty.PropertyName:
                    if (isFeatureEnabled('cal-onlineMeetingService') && value) {
                        onlineMeetingProviderProperties.push({
                            key: 'links.options',
                            value,
                        });
                    }
                    break;
                case SchedulingServiceUpdateUrlExtendedProperty.PropertyName:
                    if (isFeatureEnabled('cal-onlineMeetingService') && value) {
                        onlineMeetingProviderProperties.push({
                            key: 'links.update',
                            value,
                        });
                    }
                    break;
                case CalendarAITimeInsightsExtendedProperty.PropertyName:
                    if (isFeatureEnabled('time-copilot-read-form-insights') && value) {
                        calendarAITimeInsights = value;
                    }
                    break;
                case LegacyEventifyEventTypeExtendedProperty.PropertyName:
                case TeamRhythmEventTypeExtendedProperty.PropertyName:
                    // Team Rhythm Extended properties mapping
                    if (isFeatureEnabled('cal-teamrhythm-extended-properties-enabled')) {
                        teamRhythmEventType = value;
                    }
                    break;
                case EventPreparationRecordExtendedProperty.PropertyName:
                    if (isFeatureEnabled('time-copilot-meeting-prep-v4-scenario') && value) {
                        eventPreparationRecord = value;
                    }
                    break;
                default:
                    break;
            }

            // Service typing is incorrect, this prop exists for full calendar items
            // and is sent to the service as part of event creation.
            if (isFeatureEnabled('cal-onlineMeetingService')) {
                const onlineMeetingProp = (
                    calendarItem as {
                        [key: string]: any;
                    }
                )['OnlineMeeting'];
                onlineMeeting = {
                    joinUrl: onlineMeetingProp?.joinUrl ?? undefined,
                    meetingDetails: onlineMeetingProp?.meetingDetails ?? undefined,
                    providerType: onlineMeetingProp?.providerType ?? undefined,
                    providerProperties: onlineMeetingProviderProperties,
                };
            }
        }
    }
    let eventStart: OwaDate | null = null;
    let eventEnd: OwaDate | null = null;

    if (calendarItem?.Start) {
        eventStart = parseCalendarItemDate(calendarItem.Start);
    }
    if (calendarItem?.End) {
        eventEnd = parseCalendarItemDate(calendarItem.End);
    }

    let recurrence = null;
    // calendarItem passed in from different path is different. It can be a partial graphQL type or pure service calendarItem
    // will clean it up when all OWS types are replaced and tracisiton to app grateway is done
    // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/122681
    if (calendarItem.Recurrence) {
        if ('__typename' in calendarItem.Recurrence) {
            recurrence = calendarItem.Recurrence;
        } else {
            recurrence = convertRecurrenceTypeToGql(calendarItem.Recurrence);
        }
    }

    let lastModifiedTime: OwaDate | null = null;
    let appointmentReplyTime: OwaDate | null = null;

    if (calendarItem.LastModifiedTime) {
        lastModifiedTime = parseCalendarItemDate(calendarItem.LastModifiedTime);
    }
    if (calendarItem.AppointmentReplyTime) {
        appointmentReplyTime = parseCalendarItemDate(calendarItem.AppointmentReplyTime);
    }

    // Copy then overwrite with proper conversions
    const ev = {
        ...{
            id: calendarItem.ItemId?.Id ?? '',
            ItemId: calendarItem.ItemId
                ? {
                      Id: calendarItem.ItemId.Id,
                      ChangeKey: calendarItem.ItemId.ChangeKey,
                      mailboxInfo,
                  }
                : undefined,
            ParentFolderId: calendarItem.ParentFolderId
                ? {
                      Id: calendarItem.ParentFolderId.Id,
                      ChangeKey: calendarItem.ParentFolderId.ChangeKey,
                      mailboxInfo,
                  }
                : undefined,
            Start: eventStart ? getISOString(eventStart) : undefined,
            End: eventEnd ? getISOString(eventEnd) : undefined,
            StartTimeZoneId: cleanTimeZoneId(calendarItem.StartTimeZoneId),
            EndTimeZoneId: cleanTimeZoneId(calendarItem.EndTimeZoneId),
            LastModifiedTime: lastModifiedTime ? getISOString(lastModifiedTime) : undefined,
            FreeBusyType: calendarItem.FreeBusyType as GqlBusyType,
            ResponseType: calendarItem.ResponseType as ResponseType,
            AppointmentReplyTime: appointmentReplyTime
                ? getISOString(appointmentReplyTime)
                : undefined,
            SeriesMasterItemId: calendarItem.SeriesMasterItemId
                ? {
                      Id: calendarItem.SeriesMasterItemId.Id,
                      ChangeKey: calendarItem.SeriesMasterItemId.ChangeKey,
                      mailboxInfo,
                  }
                : null,
            EventPollMainItemId: calendarItem.EventPollMainItemId
                ? {
                      Id: calendarItem.EventPollMainItemId.Id,
                      ChangeKey: calendarItem.EventPollMainItemId.ChangeKey,
                      mailboxInfo,
                  }
                : null,

            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/43633
            // Treat any "private" group calendar events as if it was a normal event.
            // The only drawback I see with this is that IF one creates such a "private" event, say,
            // using Desktop Outlook, then it will show the lock icon and we won't. I don't think this
            // is a big deal, but if we decide that it is, then we will need to modify any other code that
            // depends on this property, beginning with owa-calendar-event-capabilities canEdit/canModify.
            Sensitivity:
                mailboxInfo?.type == 'GroupMailbox' && calendarItem.Sensitivity == 'Private'
                    ? 'Normal'
                    : calendarItem.Sensitivity,
            Preview: calendarItem.Preview ? getDisplayPreviewText(calendarItem.Preview) : undefined,
            RequiredAttendees: calendarItem.RequiredAttendees?.map(getGqlAttendee),
            Resources: calendarItem.Resources?.map(getGqlAttendee),
            OptionalAttendees: calendarItem.OptionalAttendees?.map(getGqlAttendee),

            // Additional (Extended) Properties
            IsBookedFreeBlocks: isBookedFreeBlocks,
            CollabSpace: collabSpace,
            ClpLabelProperty: clpLabelProperty,
            ClpLabelModificationHeader: clpLabelModificationHeader,
            InboxReminders: convertInboxRemindersToGql(calendarItem.InboxReminders),
            AppendOnSend: appendOnSend,
            PrependOnSend: prependOnSend,
            DocLinks: calendarItem.DocLinks?.map(convertDocLinkToGql),
            Recurrence: recurrence,
            Attachments: calendarItem.Attachments?.map(convertAttachmentToGql),
            SkypeTeamsProperties:
                calendarItem.SkypeTeamsProperties &&
                parseSkypeTeamsProperties(calendarItem.SkypeTeamsProperties),
            UserDefinedImportanceScore: userDefinedImportanceScore,
            CopilotImportanceScore: copilotImportanceScore,
            CopilotImportanceReason: copilotImportanceReason,
            CopilotImportanceReasonShort: copilotImportanceReasonShort,
            CopilotImportancePromptVersion: copilotImportancePromptVersion,
            Intent: intent,
            CopilotManagedEventType: copilotManagedEventType,
            CalendarAITimeInsights: calendarAITimeInsights,
            CopilotAutoReschedulingCount: copilotAutoReschedulingCount,
            EventPollId: eventPollId,
            EventPollChoices: eventPollChoices,
            EventPollDuration: eventPollDuration,
            EventPollMetadata: eventPollMetadata,
            EventPollTimeZone: eventPollTimeZone,
            EventPollFinalizedChoice: eventPollFinalizedChoice,
            EventPollUpdateUrl: eventPollUpdateUrl,
            EventPollVotingUrl: eventPollVotingUrl,
            EventPollExpiration: eventPollExpiration,
            EventPreparationRecord: eventPreparationRecord,

            // 1:1 mappings
            Body: calendarItem.Body,
            Categories: calendarItem.Categories,
            ConversationId: calendarItem.ConversationId,
            DateTimeCreated: calendarItem.DateTimeCreated,
            EffectiveRights: calendarItem.EffectiveRights,
            EntityNamesMap: calendarItem.EntityNamesMap,
            HasAttachments: calendarItem.HasAttachments,
            HasBlockedImages: calendarItem.HasBlockedImages,
            InReplyTo: calendarItem.InReplyTo,
            InstanceKey: calendarItem.InstanceKey,
            IsDraft: calendarItem.IsDraft,
            ReminderIsSet: calendarItem.ReminderIsSet,
            ReminderMinutesBeforeStart: calendarItem.ReminderMinutesBeforeStart,
            Subject: calendarItem.Subject,
            TailoredXpEntities: calendarItem.TailoredXpEntities,
            AppointmentReplyName: calendarItem.AppointmentReplyName,
            CalendarItemType: calendarItem.CalendarItemType,
            CalendarEventClassifications: calendarItem.CalendarEventClassifications,
            CharmId: calendarItem.CharmId,
            ClientSeriesId: calendarItem.ClientSeriesId,
            DoNotForwardMeeting: calendarItem.DoNotForwardMeeting,
            ExtractionSourceId: calendarItem.ExtractionSourceId,
            HideAttendees: calendarItem.HideAttendees,
            IntendedFreeBusyStatus: calendarItem.IntendedFreeBusyStatus,
            IsAllDayEvent: calendarItem.IsAllDayEvent,
            IsMeeting: calendarItem.IsMeeting,
            IsCancelled: calendarItem.IsCancelled,
            IsOnlineMeeting: calendarItem.IsOnlineMeeting,
            IsOrganizer: calendarItem.IsOrganizer,
            IsCoOrganizer: calendarItem.IsCoOrganizer,
            IsRecurring: calendarItem.IsRecurring,
            IsResponseRequested: calendarItem.IsResponseRequested,
            IsRoomRequested: calendarItem.IsRoomRequested,
            IsSeriesCancelled: calendarItem.IsSeriesCancelled,
            Location: calendarItem?.Location
                ? getLocationDisplayWithAddressText(
                      calendarItem.Location?.DisplayName || '',
                      calendarItem.Location?.PostalAddress
                  )
                : undefined,
            Locations: calendarItem.Locations,
            MeetingRequestWasSent: calendarItem.MeetingRequestWasSent,
            MentionsEx: calendarItem.MentionsEx,
            OnlineMeetingProvider: calendarItem.OnlineMeetingProvider,
            OnlineMeetingJoinUrl: calendarItem.OnlineMeetingJoinUrl,
            OnlineMeetingConferenceId: calendarItem.OnlineMeetingConferenceId,
            OnlineMeetingTollNumber: calendarItem.OnlineMeetingTollNumber,
            OnlineMeetingTollFreeNumbers: calendarItem.OnlineMeetingTollFreeNumbers,
            Organizer: calendarItem.Organizer,
            SeriesId: calendarItem.SeriesId,
            UID: calendarItem.UID,
            AssociatedTasks: calendarItem.AssociatedTasks,
            ResponseMode: calendarItem.ResponseMode,
            Attendance: calendarItem.Attendance,
            MuteNotifications: calendarItem.MuteNotifications,
            RightsManagementLicenseData: calendarItem.RightsManagementLicenseData,
            AllowNewTimeProposal: calendarItem.AllowNewTimeProposal,
            ConflictResolution: conflictResolution,
            VirtualAppointment: virtualAppointment,
            VirtualEvent: virtualEvent,
            MeetingTemplateId: meetingTemplateId,
            RequestedAttendanceMode:
                calendarItem.RequestedAttendanceMode as RequestedAttendanceMode,
            OnlineMeeting: onlineMeeting,
            AttendanceSource: calendarItem.AttendanceSource,
            IsFollowableMeeting: calendarItem.IsFollowableMeeting,
            IsPresenter: isPresenter,
            TeamRhythmEventType: teamRhythmEventType,
        },
    } as GqlCalendarEvent;

    return ev;
}

function parseSkypeTeamsProperties(skypeTeamsProperties: string) {
    try {
        return JSON.parse(skypeTeamsProperties);
    } catch (e) {
        return undefined;
    }
}

export function parseCalendarItemDate(date: string): OwaDate {
    // All production calls to calendarEvent() are done in response to
    // an EWS call or notification, which returns data in the user's time zone.
    // We can parse making this assumption, to handle dates outside the known DST
    // ranges without losing the offset returned by the server.
    const assumeDatesAreInThisTz = getConfig().timezone as string;
    const assumeOffsetFromStringIsCorrect = !!assumeDatesAreInThisTz;
    return parse(assumeDatesAreInThisTz, date, assumeOffsetFromStringIsCorrect);
}

function getGqlAttendee(attendee: AttendeeType): Attendee {
    return {
        Mailbox: attendee.Mailbox ?? undefined,
        ResponseType: attendee.ResponseType ?? undefined,
        LastResponseTime: attendee.LastResponseTime ?? undefined,
        ResponseComment: attendee.ResponseComment ?? undefined,
        ProposedStart: attendee.ProposedStart ?? undefined,
        ProposedEnd: attendee.ProposedEnd ?? undefined,
        IsCoauthor: attendee.IsCoauthor ?? false,
        ResponseMode: attendee.ResponseMode ?? undefined,
        Attendance: attendee.Attendance ?? undefined,
        AttendanceSource: attendee.AttendanceSource ?? undefined,
    };
}
